import { GenericState } from '../../store/wrapper/sliceWrapper';
import {
  characterAndFewSpecialCharacters,
  emptyStringRegex,
  onlyCharacterAndDotRegex
} from '../regex';
import IUserList from '../UserList/UserList.interface';
import { decrypt } from './encryption';
import { responseMessages, SSOErrorMessage } from './enums';

export const AWS_SIGNIN_URL =
  'https://signin.aws.amazon.com/federation?Action=login&Destination=https://console.aws.amazon.com/codecommit/&SigninToken=';

export const colors = [
  'rgba(54, 162, 235, 0.7)',
  'rgba(255, 99, 132, 0.7)',
  'rgba(255, 159, 64, 0.7)',
  'rgba(255, 205, 86, 0.7)',
  'rgba(75, 192, 192, 0.7)',
  'rgba(153, 102, 255, 0.7)',
  'rgba(201, 203, 207, 0.7)',
  'rgba(161, 77, 160, 0.7)',
  'rgba(1, 240, 139, 0.7)',
  'rgba(255, 44, 44, 0.7)',
  'rgba(65, 232, 255, 0.7)',
  'rgba(49, 85, 255, 0.7)',
  'rgba(212, 88, 255, 0.7)',
  'rgba(141, 228, 0, 0.7)',
  'rgba(93, 53, 255, 0.7)',
  'rgba(4, 133, 117, 0.7)',
  'rgba(4, 188, 67, 0.7)',
  'rgba(228, 0, 150, 0.7)',
  'rgba(106, 191, 203, 0.7)',
  'rgba(255, 136, 222, 0.7)',
  'rgba(228, 96, 0, 0.7)',
  'rgba(0, 255, 127, 0.7)',
  'rgba(255, 20, 147, 0.7)',
  'rgba(0, 191, 255, 0.7)',
  'rgba(255, 215, 0, 0.7)',
  'rgba(138, 43, 226, 0.7)',
  'rgba(255, 69, 0, 0.7)',
  'rgba(0, 250, 154, 0.7)',
  'rgba(70, 130, 180, 0.7)',
  'rgba(255, 182, 193, 0.7)',
  'rgba(154, 205, 50, 0.7)'
];

export const borderColors = [
  'rgba(54, 162, 235, 1)',
  'rgba(255, 99, 132, 1)',
  'rgba(255, 159, 64, 1)',
  'rgba(255, 205, 86, 1)',
  'rgba(75, 192, 192, 1)',
  'rgba(153, 102, 255, 1)',
  'rgba(201, 203, 207, 1)',
  'rgba(161, 77, 160, 1)',
  'rgba(1, 240, 139, 1)',
  'rgba(255, 44, 44, 1)',
  'rgba(65, 232, 255, 1)',
  'rgba(49, 85, 255, 1)',
  'rgba(212, 88, 255, 1)',
  'rgba(141, 228, 0, 1)',
  'rgba(93, 53, 255, 1)',
  'rgba(4, 133, 117, 1)',
  'rgba(4, 188, 67, 1)',
  'rgba(228, 0, 150, 1)',
  'rgba(106, 191, 203, 1)',
  'rgba(255, 136, 222, 1)',
  'rgba(228, 96, 0, 1)',
  'rgba(0, 255, 127, 1)',
  'rgba(255, 20, 147, 1)',
  'rgba(0, 191, 255, 1)',
  'rgba(255, 215, 0, 1)',
  'rgba(138, 43, 226, 1)',
  'rgba(255, 69, 0, 1)',
  'rgba(0, 250, 154, 1)',
  'rgba(70, 130, 180, 1)',
  'rgba(255, 182, 193, 1)',
  'rgba(154, 205, 50, 1)'
];

export const graphOptions = {
  responsive: true,
  plugins: {
    legend: {
      display: false
    }
  }
};

export const removeDuplicates = (arr: IUserList[]): IUserList[] => {
  const uniqueEmails = new Set();
  return arr.filter((obj) => {
    const isDuplicate = uniqueEmails.has(obj.email);
    uniqueEmails.add(obj.email);
    return !isDuplicate;
  });
};
// Takes string as argument and returns true if no error else returns an error message
export const validateNamePattern = (v: string): string | boolean => {
  if (!v.trim()) {
    return true;
  }
  if (!onlyCharacterAndDotRegex.test(v)) {
    return 'Numbers and special characters are not allowed except . - \'';
  }
  return true;
};
// Takes string as argument and returns true if so error else returns a error message
export const validateAddressPattern = (v: string): string | boolean => {
  if (!v.trim()) {
    return true;
  }
  if (!characterAndFewSpecialCharacters.test(v)) {
    return 'Can not use special characters except / , . - ';
  }
  return true;
};
// Takes string as argument and returs true if string not empty else returns false
export const isNotEmpty = (v: string): boolean => {
  return emptyStringRegex.test(v);
};

export const clearSession = () => {
  sessionStorage.clear();
};

export const getJwtTokenFromSession = () => {
  const encryptedToken = sessionStorage.getItem('jwtToken');
  const jwtToken = decrypt(encryptedToken);
  return jwtToken;
};

export const getLoadingState = (state: GenericState<any>): boolean => {
  return !state.data && state.status === 'loading';
};

export const getSyncingState = (state: GenericState<any>): boolean => {
  return state.data && state.status === 'loading';
};

export const getErrorMessage = (description: string | null): string | undefined => {
  let message = '';
  if (!description) return;
  if (description?.toLocaleLowerCase().includes('user not authorized')) {
    message = SSOErrorMessage.UNAUTHORIZED;
  } else if (description?.toLocaleLowerCase().includes('user is not confirmed')) {
    message = SSOErrorMessage.NOTCONFIRMED;
  } else {
    message = responseMessages.SomethingWentWrong;
  }
  return message;
};

export const shadeRGBColor = (rgba: string, factor = 0.8): string => {
  const rgbaValue = rgba.match(/\d+(\.\d+)?/g);
  if (rgbaValue) {
    const [r, g, b, a] = rgbaValue.map(Number);
    const newR = Math.round(r * factor);
    const newG = Math.round(g * factor);
    const newB = Math.round(b * factor);
    return `rgba(${newR}, ${newG}, ${newB}, ${a})`;
  } else {
    return rgba;
  }
};
